import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, RotateCcw, AlertTriangle, RefreshCw, Users, Package, FileText, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getTrash, restoreTrashItem, purgeTrash } from '../../config/api';

const GROUPS = [
  { key: 'customers', type: 'customer', label: 'Customers', icon: Users, primary: (x) => x.name, secondary: () => null },
  { key: 'products', type: 'product', label: 'Products', icon: Package, primary: (x) => x.name, secondary: (x) => x.ndc11 },
  { key: 'contracts', type: 'contract', label: 'Contracts', icon: FileText, primary: (x) => x.number, secondary: () => null }
];

const fmtDate = (v) => (v ? new Date(v).toLocaleString() : '—');

const TrashManagement = () => {
  const [data, setData] = useState({ counts: {}, customers: [], products: [], contracts: [] });
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await getTrash()); }
    catch (e) { toast.error('Failed to load trash'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const restore = async (type, id) => {
    try { await restoreTrashItem(type, id); toast.success('Restored'); load(); }
    catch (e) { toast.error('Failed to restore'); }
  };

  const doPurge = async () => {
    setPurging(true);
    try {
      const res = await purgeTrash();
      const r = res.data || {};
      const purged = (r.customers?.purged || 0) + (r.products?.purged || 0) + (r.contracts?.purged || 0);
      const skipped = (r.customers?.skipped || 0) + (r.products?.skipped || 0) + (r.contracts?.skipped || 0);
      toast.success(`Purged ${purged} record(s)` + (skipped ? `, ${skipped} skipped (still referenced)` : ''));
      setConfirm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to purge');
    } finally { setPurging(false); }
  };

  const total = data.counts?.total || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Trash2 className="h-5 w-5 text-gray-500" /> Trash</h2>
          <p className="text-sm text-gray-500">Records marked for deletion. Restore them, or permanently purge everything.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} title="Refresh" className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></button>
          <button onClick={() => setConfirm(true)} disabled={total === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
            <Trash2 className="h-4 w-4" /> Purge all ({total})
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
      ) : total === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-12 text-center text-gray-500">Trash is empty — nothing is marked for deletion.</div>
      ) : (
        <div className="space-y-5">
          {GROUPS.map((g) => {
            const items = data[g.key] || [];
            if (items.length === 0) return null;
            const Icon = g.icon;
            return (
              <div key={g.key} className="rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
                  <Icon className="h-4 w-4 text-gray-500" />
                  <h3 className="text-sm font-semibold text-gray-900">{g.label}</h3>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{items.length}</span>
                </div>
                <ul className="divide-y divide-gray-100">
                  {items.map((x) => (
                    <li key={x.id} className="flex items-center justify-between px-4 py-2.5">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{g.primary(x) || '—'}</p>
                        <p className="text-xs text-gray-400">{g.secondary(x) ? g.secondary(x) + ' · ' : ''}deleted {fmtDate(x.deletedAt)}</p>
                      </div>
                      <button onClick={() => restore(g.type, x.id)} title="Restore"
                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                        <RotateCcw className="h-3.5 w-3.5" /> Restore
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {/* Purge confirmation */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-center gap-2 text-red-600"><AlertTriangle className="h-5 w-5" /><h3 className="text-lg font-semibold">Permanently delete?</h3></div>
            <p className="mt-2 text-sm text-gray-600">
              This will <strong>permanently remove all {total} record(s)</strong> marked for deletion — this cannot be undone.
              Records still referenced by other data (e.g. a customer with claims) are skipped.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConfirm(false)} className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={doPurge} disabled={purging}
                className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                {purging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Purge all
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrashManagement;
